import React, { memo, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User } from "../../../interfaces/entities/user";
import { Collection } from "../../../enums/collections.enum";
import Button from "@mui/material/Button";
import { Card, CardActionArea, CardActions, CardContent, CardMedia, Typography } from "@mui/material";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import { PasswordConfirmationDialog } from "../../shared/confirmationDialog/PasswordConfirmationDialog";
import { useDeleteEmployeeWithDependanciesMutation } from "../../../api/userApi";
import { useToast } from "../../../hooks/useToast";
import { useLoader } from "../../../hooks/useLoader";
import { adminPassword } from "../../../utils/config";

interface EmployeeViewProps {
  userData: User;
  companyId: string;
  roleLabel: string;
}

// Memoized card view component for better list rendering performance
// Prevents unnecessary re-renders when parent list updates
export const OwnerCardView: React.FC<EmployeeViewProps> = memo(({ userData, companyId, roleLabel }) => {

  const { currentUserIsOwnerOf, isAdmin } = useAuthenticatedUser();
  const navigate = useNavigate();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteEmployee] = useDeleteEmployeeWithDependanciesMutation();
  const { showSuccess, showError } = useToast();
  const { showLoader, hideLoader } = useLoader();

  // Memoize the card style to prevent style recalculation
  const cardStyle = useMemo(() => ({
    maxWidth: { sm: '100%', md: '250px' } as const,
    backgroundColor: '#FFFFFF50'
  }), []);

  // Memoize the card media image source
  const imageSrc = useMemo(() => userData.image || undefined, [userData.image]);

  // Memoize the view link target
  const viewLinkTarget = useMemo(() =>
    `/${Collection.Companies}/${companyId}/Users/${userData.id}/edit`,
    [userData.id, companyId]
  );

  const handleDeleteClick = () => {
    setErrorMessage('');
    setPasswordDialogOpen(true);
  };

  const handlePasswordConfirm = async (enteredPassword: string) => {
    // Verify admin password
    if (enteredPassword === adminPassword) {
      try {
        showLoader("Deleting owner...");
        await deleteEmployee({ userId: userData.id }).unwrap();
        hideLoader();
        showSuccess("Owner deleted successfully");
        // Navigate to the companies page
        navigate(`/${Collection.Companies}`);
      } catch (error: any) {
        hideLoader();
        showError(error?.message || "Failed to delete owner");
      }
      setPasswordDialogOpen(false);
    } else {
      setErrorMessage('Incorrect password. Please try again.');
    }
  };

  const handlePasswordCancel = () => {
    setPasswordDialogOpen(false);
    setErrorMessage('');
  };

  return (
    <Card sx={cardStyle}>
      <CardActionArea>
        <CardMedia
          component="img"
          height="250"
          image={imageSrc}
          alt={`${userData.firstName} ${userData.lastName}`}
        />

        <CardContent>
          <Typography gutterBottom variant="h5" component="div">
            {userData.firstName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {roleLabel}
          </Typography>
        </CardContent>
      </CardActionArea>

      <CardActions>
        {(isAdmin || currentUserIsOwnerOf(companyId)) && <Button
          size="small"
          color="primary"
          component={Link}
          to={viewLinkTarget}
        >
          EDIT
        </Button>}
        {isAdmin && <Button
          size="small"
          color="error"
          onClick={handleDeleteClick}
        >
          DELETE
        </Button>}
      </CardActions>

      <PasswordConfirmationDialog
        open={passwordDialogOpen}
        title="Delete Owner"
        message={errorMessage || `Please enter admin password to delete ${userData.firstName} ${userData.lastName}. This action cannot be undone.`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        onConfirm={handlePasswordConfirm}
        onCancel={handlePasswordCancel}
      />
    </Card>
  );
});

